import { safeLocal } from '../safeStorage'

/** احتياطي محلي إذا تعذّر استرجاع سياق الجهاز من قاعدة البيانات في /success */
export const PURCHASE_DEVICE_STORAGE_KEY = 'eh_purchase_device'

/**
 * لقطة سياق الجهاز/المتصفح عند الدفع — تُخزَّن مع الطلب وتُستعاد في /success/<token>
 * لأن صفحة النجاح قد تُفتح بمتصفح/وكيل مختلف بعد بوابة Tap.
 */
export type DeviceContext = {
  user_agent: string
  device_type: string
  operating_system: string
  browser_name: string
}

function getDeviceType(ua: string): string {
  if (/iPhone/i.test(ua)) return 'iPhone'
  if (/iPad/i.test(ua)) return 'iPad'
  if (/iPod/i.test(ua)) return 'iPod'
  if (/Android/i.test(ua)) {
    if (/tablet|sm-t\d|gt-p\d/i.test(ua)) return 'Android Tablet'
    return 'Android Phone'
  }
  if (/Windows Phone/i.test(ua)) return 'Windows Phone'
  if (/Windows/i.test(ua)) return 'Windows PC'
  if (/Macintosh|Mac OS X/i.test(ua)) return 'Mac'
  if (/Linux/i.test(ua)) return 'Linux PC'
  if (/CrOS/i.test(ua)) return 'Chromebook'
  return 'Unknown'
}

function getBrowserName(ua: string): string {
  if (/Edg/i.test(ua)) return 'Microsoft Edge'
  if (/Chrome/i.test(ua) && !/Chromium/i.test(ua)) return 'Chrome'
  if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return 'Safari'
  if (/Firefox/i.test(ua)) return 'Firefox'
  if (/Opera|OPR/i.test(ua)) return 'Opera'
  if (/Trident|MSIE/i.test(ua)) return 'Internet Explorer'
  return 'Unknown'
}

function getOperatingSystem(ua: string): string {
  const iosMatch = ua.match(/OS (\d+)_(\d+)_?(\d+)?/)
  if (iosMatch) {
    return `iOS ${iosMatch[1]}.${iosMatch[2]}${iosMatch[3] ? '.' + iosMatch[3] : ''}`
  }
  const androidMatch = ua.match(/Android (\d+(?:\.\d+)?)/)
  if (androidMatch) return `Android ${androidMatch[1]}`
  if (/Windows NT 10.0/i.test(ua)) return 'Windows 10'
  const macMatch = ua.match(/Mac OS X (\d+)_(\d+)_?(\d+)?/)
  if (macMatch) {
    return `macOS ${macMatch[1]}.${macMatch[2]}${macMatch[3] ? '.' + macMatch[3] : ''}`
  }
  if (/Linux/i.test(ua)) return 'Linux'
  if (/CrOS/i.test(ua)) return 'Chrome OS'
  return 'Unknown'
}

/** لقطة الجهاز الحالي (عادةً عند بدء الدفع على checkout) */
export function captureDeviceContext(): DeviceContext | null {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return null
  const user_agent = navigator.userAgent
  return {
    user_agent,
    device_type: getDeviceType(user_agent),
    operating_system: getOperatingSystem(user_agent),
    browser_name: getBrowserName(user_agent),
  }
}

export function persistPurchaseDeviceContext(): void {
  const ctx = captureDeviceContext()
  if (!ctx) return
  try {
    safeLocal.setItem(PURCHASE_DEVICE_STORAGE_KEY, JSON.stringify(ctx))
  } catch {
    /* noop */
  }
}

export function loadPersistedPurchaseDeviceContext(): DeviceContext | null {
  try {
    const raw = safeLocal.getItem(PURCHASE_DEVICE_STORAGE_KEY)
    if (!raw) return null
    return deviceContextFromUnknown(JSON.parse(raw))
  } catch {
    return null
  }
}

export function deviceContextFromUnknown(raw: unknown): DeviceContext | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const user_agent = typeof o.user_agent === 'string' ? o.user_agent : ''
  if (!user_agent.trim()) return null
  return {
    user_agent,
    device_type: typeof o.device_type === 'string' ? o.device_type : getDeviceType(user_agent),
    operating_system:
      typeof o.operating_system === 'string' ? o.operating_system : getOperatingSystem(user_agent),
    browser_name: typeof o.browser_name === 'string' ? o.browser_name : getBrowserName(user_agent),
  }
}
